import { useState } from "react";
import { Plus, Send, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  useCampaigns,
  useDeleteCampaign,
  useSendCampaign,
} from "@/hooks/useCampaigns";
import { CampaignModalV2 } from "@/components/modals/CampaignModalV2";
import { CampaignResponse } from "@/types/api";

export default function Campaigns() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] =
    useState<CampaignResponse | null>(null);

  const { data: campaigns, isLoading, error } = useCampaigns();
  const deleteCampaign = useDeleteCampaign();
  const sendCampaign = useSendCampaign();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "sending":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "draft":
        return "Rascunho";
      case "sending":
        return "Enviando";
      case "completed":
        return "Concluída";
      case "failed":
        return "Falhou";
      default:
        return status;
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Tem certeza que deseja excluir esta campanha?")) {
      deleteCampaign.mutate(id);
    }
  };

  const handleSend = (id: string) => {
    if (confirm("Tem certeza que deseja enviar esta campanha?")) {
      sendCampaign.mutate(id);
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Campanhas WhatsApp</h1>
        <div className="text-red-500">
          Erro ao carregar campanhas: {error.message}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6">Carregando campanhas...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Campanhas WhatsApp</h1>
        <div className="flex gap-2">
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Campanha
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {campaigns && campaigns.length > 0 ? (
          campaigns.map((campaign) => (
            <Card key={campaign.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-lg">{campaign.name}</CardTitle>
                <Badge className={getStatusColor(campaign.status)}>
                  {getStatusText(campaign.status)}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    {campaign.description && (
                      <p className="text-sm text-muted-foreground">
                        {campaign.description}
                      </p>
                    )}
                    <p className="text-sm">
                      {campaign.sentCount || 0} de {campaign.totalLeads || 0}{" "}
                      leads
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Criada em{" "}
                      {new Date(campaign.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {campaign.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => handleSend(campaign.id)}
                        disabled={sendCampaign.isPending}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Enviar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingCampaign(campaign);
                        setIsModalOpen(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(campaign.id)}
                      disabled={deleteCampaign.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            Nenhuma campanha encontrada
          </div>
        )}
      </div>

      <CampaignModalV2
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCampaign(null);
        }}
        campaign={editingCampaign}
      />
    </div>
  );
}
